import { Font } from '@react-pdf/renderer';

/**
 * 简化版中文断词系统
 * 避免复杂的语法问题，专注于核心功能
 */

// 检测中文字符
const isChinese = (char) => {
  const code = char.charCodeAt(0);
  return (code >= 0x4e00 && code <= 0x9fff) || // 基本汉字
         (code >= 0x3400 && code <= 0x4dbf);   // 扩展A
};

// 检测英文字符
const isEnglish = (char) => {
  const code = char.charCodeAt(0);
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
};

// 检测数字
const isNumber = (char) => {
  const code = char.charCodeAt(0);
  return code >= 48 && code <= 57;
};

// 简单的中文断词函数
const simpleChineseHyphenation = (word) => {
  if (!word || word.length <= 1) {
    return [word];
  }

  const result = [];
  let currentSegment = '';

  for (let i = 0; i < word.length; i++) {
    const char = word[i];
    currentSegment += char;

    // 判断是否可以断行
    let canBreak = false;

    if (i === word.length - 1) {
      // 最后一个字符
      canBreak = true;
    } else if (isChinese(char)) {
      // 中文字符后可以断行
      canBreak = true;
    } else if (char === ' ') {
      // 空格后可以断行
      canBreak = true;
    } else if (!isEnglish(char) && !isNumber(char)) {
      // 非英文非数字字符后可以断行
      canBreak = true;
    }

    if (canBreak && currentSegment.length >= 1) {
      result.push(currentSegment);
      currentSegment = '';
    }
  }

  if (currentSegment) {
    if (result.length > 0) {
      result[result.length - 1] += currentSegment;
    } else {
      result.push(currentSegment);
    }
  }

  return result.length > 0 ? result : [word];
};

// 更智能的中文断词函数
const smartChineseHyphenation = (word) => {
  if (!word || word.length <= 2) {
    return [word];
  }

  const segments = [];
  let currentSegment = '';
  
  for (let i = 0; i < word.length; i++) {
    const char = word[i];

    currentSegment += char;
    
    // 检查是否应该在此处断开
    const shouldBreak = (
      i === word.length - 1 || // 最后一个字符
      (isChinese(char) && currentSegment.length >= 1) || // 中文字符
      (char === ' ') || // 空格
      (char === ',' || char === '.' || char === '!' || char === '?') // 常见标点
    );
    
    if (shouldBreak && currentSegment.trim()) {
      segments.push(currentSegment);
      currentSegment = '';
    }
  }
  
  if (currentSegment.trim()) {
    segments.push(currentSegment);
  }
  
  return segments.length > 0 ? segments : [word];
};

// 无连字符的中文断词函数 - 专门解决连字符问题
const noHyphenChineseBreaking = (word) => {
  if (!word || word.length <= 1) {
    return [word];
  }

  // 这个函数的目标是提供断行点，但绝对不修改原始文本
  // 避免任何可能导致连字符的操作
  const result = [];
  let currentSegment = '';

  for (let i = 0; i < word.length; i++) {
    const char = word[i];
    currentSegment += char;

    // 在中文字符后可以断行，英文单词保持完整
    if (isChinese(char) || char === ' ' || i === word.length - 1) {
      // 直接推入原始文本片段，不做任何修改
      result.push(currentSegment);
      currentSegment = '';
    }
  }

  // 处理剩余内容
  if (currentSegment) {
    if (result.length > 0) {
      result[result.length - 1] += currentSegment;
    } else {
      result.push(currentSegment);
    }
  }

  return result.length > 0 ? result : [word];
};

// 注册中文断词回调
export const registerSimpleChineseHyphenation = (mode = 'smart') => {
  let callback;

  if (mode === 'simple') {
    callback = simpleChineseHyphenation;
  } else if (mode === 'no-hyphen') {
    callback = noHyphenChineseBreaking;
  } else {
    callback = smartChineseHyphenation;
  }

  try {
    Font.registerHyphenationCallback(callback);
    console.log(`简化中文断词已注册 (模式: ${mode})`);
  } catch (error) {
    console.warn('断词注册失败:', error);
  }
};

// 禁用断词
export const disableHyphenation = () => {
  try {
    Font.registerHyphenationCallback(word => [word]);
    console.log('断词功能已禁用');
  } catch (error) {
    console.warn('断词禁用失败:', error);
  }
};

// 导出工具函数
export {
  isChinese,
  isEnglish,
  isNumber,
  simpleChineseHyphenation,
  smartChineseHyphenation,
  noHyphenChineseBreaking
};

/**
 * 基于React PDF官方文档和GitHub issues的中文断词解决方案
 * 参考: https://github.com/diegomura/react-pdf/issues/1568
 *
 * 核心思路：
 * 1. 对于中文字符，在每个字符后添加空字符串作为断行点
 * 2. 这样可以实现中文字符的自然换行，同时避免连字符
 * 3. 对于英文单词，保持完整性
 */
Font.registerHyphenationCallback((word) => {
  if (!word || word.length <= 1) {
    return [word];
  }

  // 检测是否包含中文字符
  const hasChinese = word.split('').some(char => isChinese(char));

  if (hasChinese) {
    // 官方推荐的方法：将每个字符分割并在后面添加空字符串
    // 这样可以在任意字符后断行，但不会显示连字符
    const result = [];
    const characters = word.split('');

    for (const char of characters) {
      result.push(char, ''); // 字符后添加空字符串作为断行点
    }

    return result;
  } else {
    // 对于纯英文文本，不进行断词以保持单词完整性
    return [word];
  }
});

console.log('已注册基于官方文档的中文断词回调（无连字符）');
